import { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Chip,
  Collapse
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  LinkOff as LinkOffIcon,
  EventBusy as EventBusyIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import useCascadeDelete from '../../hooks/useCascadeDelete';
import { pluralizeWithCount } from '../../utils/pluralize';

/**
 * CascadeDeleteDialog - Componente auto-suficiente para cascade delete em 2 fases.
 *
 * Ao abrir, automaticamente chama a API para obter preview de impacto.
 * Mostra impactos dinâmicos do backend (não mais hardcoded).
 * Ao confirmar, executa cascade delete com hash de confirmação.
 * Em caso de 409 (impacto mudou), atualiza preview automaticamente.
 *
 * @example
 * <CascadeDeleteDialog
 *   open={deleteDialog.open}
 *   onClose={deleteDialog.handleClose}
 *   onDeleted={() => { deleteDialog.handleClose(); reload(); }}
 *   deletePath={`/professionals/${deleteDialog.data?.id}`}
 *   entityName={deleteDialog.data?.name}
 * />
 */
export default function CascadeDeleteDialog({
  open,
  onClose,
  onDeleted,
  deletePath,
  entityName,
}) {
  const cascade = useCascadeDelete();

  // Ao abrir, buscar impacto
  useEffect(() => {
    if (open && deletePath) {
      cascade.requestDelete(deletePath);
    }
    if (!open) {
      cascade.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deletePath]);

  // Quando done, notificar parent
  useEffect(() => {
    if (cascade.isDone) {
      onDeleted?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cascade.isDone]);

  const hasImpacts = cascade.impact?.impactGroups?.length > 0;
  const totalAffected = cascade.impact?.totalAffectedCount || 0;

  return (
    <Dialog
      open={open}
      onClose={cascade.isDeleting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ elevation: 8 }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <WarningIcon color="warning" fontSize="large" />
        <Typography variant="h6" component="span">
          Confirmar Exclusão
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Loading state */}
        {cascade.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {cascade.state === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {cascade.error}
          </Alert>
        )}

        {/* Preview state */}
        {cascade.isPreview && cascade.impact && (
          <>
            {/* Entity name */}
            <Typography variant="body1" sx={{ mb: 2 }}>
              Tem certeza que deseja excluir{' '}
              <strong>{cascade.impact.entityName || entityName || 'este item'}</strong>?
            </Typography>

            {/* Impact changed warning */}
            <Collapse in={!!cascade.error}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {cascade.error}
              </Alert>
            </Collapse>

            {/* Impact groups */}
            {hasImpacts && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <WarningIcon fontSize="small" color="warning" />
                  Impactos desta exclusão:
                </Typography>

                <List
                  dense
                  disablePadding
                  sx={{
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mt: 1,
                  }}
                >
                  {cascade.impact.impactGroups.map((group, groupIdx) => (
                    <Box key={groupIdx}>
                      <ListItem sx={{ py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {group.action === 'cancel' ? (
                            <EventBusyIcon fontSize="small" color="warning" />
                          ) : group.action === 'unlink' ? (
                            <LinkOffIcon fontSize="small" color="info" />
                          ) : (
                            <DeleteIcon fontSize="small" color="error" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {group.displayName}
                              </Typography>
                              <Chip
                                label={group.count}
                                size="small"
                                color={group.count > 5 ? 'error' : 'warning'}
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            group.items?.length > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                              >
                                {group.items.map((item) => item.name).join(', ')}
                                {group.count > group.items.length &&
                                  ` e mais ${group.count - group.items.length}...`}
                              </Typography>
                            )
                          }
                        />
                      </ListItem>
                      {groupIdx < cascade.impact.impactGroups.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              </Box>
            )}

            {/* No impacts */}
            {!hasImpacts && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Nenhum item dependente será afetado.
              </Alert>
            )}

            {/* Confirmation alert */}
            <Alert severity={totalAffected > 0 ? 'warning' : 'info'}>
              {totalAffected > 0
                ? `${pluralizeWithCount(totalAffected, 'item dependente', 'itens dependentes')} serão afetados. Esta ação não pode ser desfeita.`
                : 'Esta ação não pode ser desfeita.'}
            </Alert>
          </>
        )}

        {/* Deleting state */}
        {cascade.isDeleting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Excluindo...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={cascade.isDeleting || cascade.isLoading}
        >
          Cancelar
        </Button>
        <Button
          onClick={cascade.confirmDelete}
          variant="contained"
          color={totalAffected > 0 ? 'error' : 'warning'}
          disabled={!cascade.isPreview || cascade.isDeleting}
          autoFocus
        >
          {cascade.isDeleting
            ? 'Excluindo...'
            : totalAffected > 0
              ? `Excluir (${totalAffected + 1} itens)`
              : 'Excluir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CascadeDeleteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func,
  deletePath: PropTypes.string,
  entityName: PropTypes.string,
};
